import * as React from 'react';
import {useState} from 'react';
import {IUser} from '../../Constants/Interfaces';
import {IMenuOption} from '../Menu/Menu';
import {User} from '../User/User';
import {useDispatch} from 'react-redux';
import {Dispatch} from '../../Redux/store';
import classNames from 'classnames';
import {Icon} from '@iconify/react';

import './List.sass';
import {INotifications} from '../../Messages';


interface IUserListProps {
    users: IUser[];
    selectedId: string;
    myId: string;
    myBuddies: string[];
    myBlacklist: string[];

    notifications: INotifications;

    placeholder: string;
}


export const UserList: React.FC<IUserListProps> = ({
    users,
    selectedId,
    myId,
    myBlacklist,
    myBuddies,

    notifications,

    placeholder,
}) => {

    const [collapsed, setCollapsed] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');

    const {users: {toggleBlock, toggleBuddy}, main: {update}} = useDispatch<Dispatch>();


    function isBlocked(uid: string, blacklist: string[]): boolean {
        const isBlockedByMe: boolean = myBlacklist.includes(uid);
        const isBlockedByUser: boolean = !!myId && blacklist.includes(myId);
        return isBlockedByMe || isBlockedByUser;
    }


    function navigateTo(selectedId: string, view: 'profile' | 'messages'): void {
        update({selectedId, viewProfile: view === 'profile'});
    }


    function getUserMenuOptions(selectedId: string, isBuddy: boolean, isBlocked: boolean): IMenuOption[] {
        return [
            {
                text: isBlocked ? 'Messaging blocked' : 'Message',
                icon: 'wpf:message',
                callback: () => navigateTo(selectedId, 'messages'),
                highlight: false,
            },
            {
                text: 'Profile',
                icon: 'fa-solid:user',
                callback: () => navigateTo(selectedId, 'profile'),
                highlight: false,
            },
            {
                text: isBuddy ? 'Unbuddy' : 'Buddy',
                icon: 'majesticons:users',
                callback: () => toggleBuddy({buddyId: selectedId, myBuddies: myBuddies}),
                highlight: isBuddy,
            },
            {
                text: myBlacklist.includes(selectedId) ? 'Unblock' : 'Block',
                icon: 'fluent:block-24-filled',
                callback: () => toggleBlock({blockedUser: selectedId, myId: myId, blacklist: myBlacklist}),
                highlight: !isBlocked,
            },
        ];
    }


    function renderUser(user: IUser) {

        if (!user) {
            return null;
        }

        const buddy = myBuddies.includes(user.uid);
        const blocked = isBlocked(user.uid, user.blacklist);
        const selected = selectedId === user.uid;
        const notification = notifications[user.uid];
        const avatar_url = user.avatar.face;
        const online = user.online;
        const name = user.name;
        const status_text = user.online ? 'online' : 'offline';
        const name_suffix = user.uid.slice(user.uid.length - 4, user.uid.length);
        const badge =
            blocked ? 'fluent:block-24-filled' :
                buddy ? 'majesticons:users' :
                    '';
        const menuOptions = getUserMenuOptions(user.uid, buddy, blocked);


        return <User
            key={user.uid}
            notification={notification}
            selected={selected}
            menu={menuOptions}
            avatar_url={avatar_url}
            top_icon={badge}
            buddy={buddy}
            online={online}
            name={name}
            status_text={status_text}
            name_suffix={name_suffix}/>;
    }


    return (
        <div className={classNames('mvc-list', {collapsed})}>
            <div className={'mvc-list-header'}>
                <Icon
                    icon={'eva:search-fill'}
                    height={16}
                    className={'mvc-list-header-left-icon'}/>

                <input
                    className={'mvc-list-header-input'}
                    placeholder={placeholder}
                    value={search}
                    onChange={({target: {value}}) => setSearch(value)}/>

                <Icon
                    icon={'eva:arrow-ios-downward-fill'}
                    height={16}
                    onClick={() => setCollapsed(!collapsed)}
                    className={'mvc-list-header-right-icon'}/>
            </div>
            {users
                .filter((user) => !search && notifications[user.uid])
                .sort((a, b) => notifications[b.uid] - notifications[a.uid])
                .map(renderUser)}
            {users
                .filter((user) => {
                    if (user.uid === selectedId) {
                        return true;
                    }
                    if (search) {
                        return (user.name + user.uid).toLowerCase().includes(search.toLowerCase());
                    }
                    else {
                        return (user.online || myBuddies.includes(user.uid)) && !notifications[user.uid];
                    }
                })
                .map(renderUser)}
        </div>
    );
};
